import React, { useState } from 'react';
import { useProductCatalogue } from '../hooks/useProductCatalogue';
import useProductImportExport from '../hooks/useImportExport';
import productCatalogueService from '../services/productCatalogueService';
import ProductCatalogueTable from '../components/productCatalogue/ProductCatalogueTable';
import ProductCatalogueForm from '../components/productCatalogue/ProductCatalogueForm';
import ProductDetails from '../components/productCatalogue/ProductDetails';
import ProductImportModal from '../components/productCatalogue/ProductImportModal';
import ProductActions from '../components/productCatalogue/ProductActions';
import ConfirmDialog from '../components/common/ConfirmDialog';
import MessageAlert from '../components/common/MessageAlert';
import { AdminGuard } from '../components/RoleGuards';
import { useErrorDialog } from '../context/errorDialogContext';
import { useTranslation } from 'react-i18next';


/**
 * ProductCataloguePage
 * - Provides UI to list, create, edit, view, delete, bulk-delete, import, export 
 *   for products. Uses multiple hooks for data operations.
 */
const ProductCataloguePage = () => {
  const { t } = useTranslation();
  // Custom hook for product data and operations
  const {
    products,
    loading,
    error,
    clearError,
    selectedProducts,
    totalCount,
    currentPage,
    totalPages,
    handlePageChange,
    filters,
    fetchProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    bulkDeleteProducts,
    handleFilterChange,
    selectProduct,
    selectAllProducts,
    hasSelection,
    selectionCount,
    exportProducts,
  } = useProductCatalogue();

  // Import/Export hook
  const {
    handleImport,
    downloadSampleTemplate,
    importing,
    importError,
    importSuccess,
    exporting,
    exportError,
    exportSuccess,
  } = useProductImportExport({
    // Provide the API callbacks so the hook can perform the actual upload/download
    onImport: productCatalogueService.importProductCatalogue,
    onExport: productCatalogueService.exportProductCatalogue,
  });



  // Local state for UI interactions
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const strings = t('productCatalogue', { returnObjects: true });
  const { showError } = useErrorDialog();

  /**
   * Global error watcher for product operations
   * - Displays errors via the global error dialog when `error` is set
   */
  React.useEffect(() => {
    if (error) {
      showError(error, strings.page?.alerts?.errorTitle);
      // Clear once shown: `error` sticks until the next request, and this effect re-runs on
      // every render because `strings.page` is a fresh object each time (t(..., { returnObjects:
      // true }) does not memoize). Leaving it set re-opens the dialog the operator just
      // dismissed, which reads as a frozen screen.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  /**
   * Handle import/export success/error messages
   */
  React.useEffect(() => {
    if (importSuccess) {
      setAlertMessage({
        type: 'success',
        title: strings.import.status.success,
        message: strings.import.notifications.success,
      });
      // Refresh data after successful import
      fetchProducts({ force: true });
    }
  }, [importSuccess, strings.import, fetchProducts]);

  React.useEffect(() => {
    if (importError) {
      setAlertMessage({
        type: 'error',
        title: strings.import.status.error,
        message: importError,
      });
    }
  }, [importError, strings.import]);

  React.useEffect(() => {
    if (exportSuccess) {
      setAlertMessage({
        type: 'success',
        title: strings.export.status.success,
        message: strings.export.notifications.success,
      });
    }
  }, [exportSuccess, strings.export]);

  React.useEffect(() => {
    if (exportError) {
      setAlertMessage({
        type: 'error',
        title: strings.export.status.error,
        message: exportError,
      });
    }
  }, [exportError, strings.export]);

  /** Refresh the products list, forcing a server reload */
  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await fetchProducts({ force: true });
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  /** Open product form for creating a new product */
  const handleCreateProduct = () => {
    setEditingProduct(null);
    setShowProductForm(true);
  };

  /** Open product form prefilled for editing the given productId */
  const handleEditProduct = async (productId) => {
    setActionLoading(true);
    try {
      const product = await getProductById(productId, { withLoading: false });
      if (product) {
        setEditingProduct(product);
        setShowProductForm(true);
      }
    } catch (err) {
      console.error('Failed to fetch product for editing:', err);
    } finally {
      setActionLoading(false);
    }
  };

  /** Open view modal and load product details for the provided productId */
  const handleViewProduct = async (productId) => {
    setViewLoading(true);
    setViewingProduct(null);
    setShowViewModal(true);
    try {
      const product = await getProductById(productId, { withLoading: false });
      if (product) {
        setViewingProduct(product);
      }
    } catch (err) {
      console.error('Failed to fetch product for viewing:', err);
    } finally {
      setViewLoading(false);
    }
  };

  /**
   * Prepare delete confirmation for a single product
   * - resolves display name for the confirm message and sets confirm state
   */
  const handleDeleteProduct = (productId) => {
    const product = products.find((p) => p.id === productId) || null;
    const displayName = product?.product_name || strings.table.copy.unnamedProduct;

    setDeleteConfirm({
      type: 'single',
      product,
      productId,
      title: strings.confirm?.deleteSingle?.title,
      message: (strings.confirm?.deleteSingle?.message || '').replace('{name}', displayName),
      confirmLabel: strings.confirm?.deleteSingle?.confirmLabel,
      loadingLabel: strings.confirm?.deleteSingle?.loadingLabel,
      cancelLabel: strings.confirm?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /**
   * Prepare delete confirmation for bulk selected products
   */
  const handleBulkDelete = () => {
    const message = selectionCount === 1
      ? strings.confirm.deleteBulk.messageSingle
      : strings.confirm.deleteBulk.messageMultiple.replace('{count}', selectionCount);

    setDeleteConfirm({
      type: 'bulk',
      count: selectionCount,
      title: strings.confirm?.deleteBulk?.title,
      message,
      confirmLabel: strings.confirm?.deleteBulk?.confirmLabel,
      loadingLabel: strings.confirm?.deleteBulk?.loadingLabel,
      cancelLabel: strings.confirm?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Execute the confirmed delete (single or bulk) operation */
  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      let result;

      if (deleteConfirm.type === 'single') {
        const idToDelete = deleteConfirm.productId || deleteConfirm.product?.id;
        if (!idToDelete) {
          throw new Error(t('productCatalogue.errors.noProductIdToDelete'));
        }
        result = await deleteProduct(idToDelete);
      } else {
        // For bulk delete ensure we pass the selected product IDs collected from the hook
        // so the API knows which items to remove.
        result = await bulkDeleteProducts(selectedProducts || []);
      }

      if (result.success) {
        setDeleteConfirm(null); // Refresh stats after delete
      } else {
        // Service returned an error - close dialog and show error message
        const errorMsg = result.error;
        setDeleteConfirm(null); // Refresh stats after error
        setAlertMessage({
          type: 'error',
          title: strings.confirm?.errorTitle,
          message: errorMsg
        });
      }
    } catch (err) {
      console.error('Delete operation failed:', err);
      // Exception occurred - close dialog and show error message
      const errorMsg = err?.message;
      setDeleteConfirm(null); // Refresh stats after error
      setAlertMessage({
        type: 'error',
        title: strings.confirm?.errorTitle,
        message: errorMsg
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle submit from the product form for create or update */
  const handleFormSubmit = async (productData) => {
    setActionLoading(true);
    try {
      let result;

      if (editingProduct) {
        result = await updateProduct(editingProduct.id, productData);
      } else {
        result = await createProduct(productData);
      }

      if (result.success) {
        setShowProductForm(false);
        setEditingProduct(null); // Refresh stats after create/update
      }

      return result;
    } catch (err) {
      console.error('Form submission failed:', err);
      return { success: false, error: 'Operation failed' };
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle product import */
  const handleProductImport = async (file) => {
    const result = await handleImport(file);
    if (result.success) {
      setShowImportModal(false);
    }
    return result;
  };

  /** Export products to Excel */
  const handleProductExport = async () => {
    setActionLoading(true);
    try {
      const result = await exportProducts();
      return result;
    } catch (err) {
      console.error('Export failed:', err);
      return { success: false, error: 'Export failed' };
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle search input change and update hook filters */
  const handleSearch = (searchTerm) => {
    handleFilterChange({ search: searchTerm });
  };

  /**
   * Count products helper
   * - Prefer the server-provided totalCount when available, otherwise fall back
   *   to the length of the current `products` array. Returns a number.
   */
  const countProducts = () => {
    if (typeof totalCount === 'number' && totalCount >= 0) return totalCount;
    if (Array.isArray(products)) return products.length;
    return 0;
  };

  // Value used by the header stat UI
  const totalProducts = countProducts();


  return (
    <AdminGuard fallback={(
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{strings.page.accessDenied.title}</h2>
          <p className="text-gray-600">{strings.page.accessDenied.message}</p>
        </div>
      </div>
    )}>
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
        {/* Alert Message */}
        {alertMessage && (
          <div className="fixed top-4 right-4 z-50">
            <MessageAlert
              type={alertMessage.type}
              title={alertMessage.title}
              message={alertMessage.message}
              onClose={() => setAlertMessage(null)}
            />
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{strings.page.header.title}</h1>
              <p className="mt-2 text-gray-600">
                {strings.page.header.subtitle}
              </p>
            </div>

            {/* Stats */}
            <div className="flex space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">{strings.page.stats.totalProducts}</div>
                  <div className="text-2xl font-bold text-gray-900">{totalProducts}</div>
                </div>
              </div>
              {hasSelection && (
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-blue-600">{strings.page.stats.selected}</div>
                    <div className="text-2xl font-bold text-blue-700">{selectionCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <ProductActions
          onAddProduct={handleCreateProduct}
          onDeleteSelected={handleBulkDelete}
          onImport={() => setShowImportModal(true)}
          onExport={handleProductExport}
          onRefresh={handleRefresh}
          onDownloadTemplate={downloadSampleTemplate}
          selectedProducts={selectedProducts}
          products={products}
          loading={actionLoading}
          refreshLoading={refreshLoading}
          importing={importing}
          exporting={exporting}
          searchValue={filters.search || ''}
          onSearchChange={handleSearch}
        />

        {/* Products Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden w-full">
          <div
            className="overflow-x-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin'
            }}
          >
            <ProductCatalogueTable
              products={products}
              loading={loading}
              selectedProducts={selectedProducts}
              onSelectProduct={selectProduct}
              onSelectAll={selectAllProducts}
              onEditProduct={handleEditProduct}
              onDeleteProduct={handleDeleteProduct}
              onViewProduct={handleViewProduct}
              viewLoading={viewLoading}
            />
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {currentPage} of {totalPages} — {totalCount} total products
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                        border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Product Form Modal */}
        {showProductForm && (
          <ProductCatalogueForm
            product={editingProduct}
            isOpen={showProductForm}
            onClose={() => {
              setShowProductForm(false);
              setEditingProduct(null);
            }}
            onSubmit={handleFormSubmit}
            loading={actionLoading}
          />
        )}

        {/* Product View Modal */}
        {showViewModal && (
          <ProductDetails
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false);
              setViewingProduct(null);
            }}
            product={viewingProduct}
            loading={viewLoading}
          />
        )}

        {/* Import Modal */}
        {showImportModal && (
          <ProductImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImport={handleProductImport}
            importing={importing}
            importError={importError}
            importSuccess={importSuccess}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <ConfirmDialog
            isOpen
            title={deleteConfirm.title}
            message={deleteConfirm.message}
            confirmLabel={deleteConfirm.confirmLabel}
            cancelLabel={deleteConfirm.cancelLabel}
            confirmVariant={deleteConfirm.confirmVariant}
            loading={actionLoading}
            loadingLabel={deleteConfirm.loadingLabel}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteConfirm(null)}
          />
        )}
      </div>
    </AdminGuard>
  );
};

export default ProductCataloguePage;